import { Injectable } from '@angular/core';
import { TelemetryData, EquipmentPayload, OutcomePayload, WaterQualityManagerPayload, ThermostatPayload, SystemPayload, SpacePayload, SchedulePayload, ScenePayload,AsyncEvent } from './interface-model/app-interface-model';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from './services/util.service';
import { environment } from '../environments/environment';
@Injectable({
  providedIn: 'root'
})
export class AppSyncWebSocketService {
  private ws: WebSocket | any;
  private telemetryData: TelemetryData | null | undefined;
  private telemetryDataSubject = new BehaviorSubject<TelemetryData | null>(null);
  private asyncStatusSubject = new BehaviorSubject<AsyncEvent | null>(null);
  private cmdRespEventSubject = new BehaviorSubject<AsyncEvent | null>(null);
  private eventUpdatesEventSubject = new BehaviorSubject<AsyncEvent | null>(null);

  constructor(private utilService: UtilService) {}
  connectWebSocket(controllerId: string) {
    const emptyPayload = 'e30='; // This is base64 encoded '{}'
    const headers = {
      host: environment.GraphQL.endpoint,
      Authorization: `Bearer ${this.utilService.getToken('idToken')}`
    };

    const headerString = btoa(JSON.stringify(headers));
    const wsUrl = `wss://${environment.GraphQL.endpoint}/graphql/realtime?header=${headerString}&payload=${emptyPayload}`;

    this.ws = new WebSocket(wsUrl, 'graphql-ws');

    this.ws.onopen = () => {
      console.log('WebSocket Connected', this.ws.url);
      // Send connection init message
      this.ws.send(JSON.stringify({
        type: 'connection_init',
        payload: {}
      }));
    };

    this.ws.onmessage = (event: any) => {
      const data = JSON.parse(event.data);

      if (data.type === 'connection_ack') {
        // console.log('connection_ack received. Starting subscriptions...');
        this.startSubscriptions(controllerId);
      }

      if (data.type == "data") {
        // console.log('Data incoming from subscription...', data)
        this.handleSubscriptionData(data.payload?.data);
      }
      console.log('Received:', data);
    };

    this.ws.onerror = (error: any) => {
      console.error('WebSocket error:', error);
    };
  }

  startSubscriptions(controllerId: string): void {
    if (!this.ws) return;
    this.dataUpdatesSubQuery(controllerId);
    this.stateUpdatesSubQuery(controllerId);
    this.cmdRespSubQuery(controllerId);
    this.eventUpdatesSubQuery(controllerId);
  }

  handleSubscriptionData(payloadData: any): void {
    if (!payloadData) return;

    // 1. stateUpdatesSub
    if (payloadData.stateUpdatesSub) {
      payloadData.stateUpdatesSub.asyncEvents?.forEach((evt: any) => {
        const parsedPayload = JSON.parse(evt.payload);
        this.asyncStatusSubject.next({ ...evt, payload: parsedPayload });
      });
    }

    // 2. dataUpdatesSub
    if (payloadData.dataUpdatesSub?.payload) {
      const teleDataRaw = payloadData.dataUpdatesSub;
      teleDataRaw.payload = JSON.parse(teleDataRaw.payload);
      this.telemetryData = this.flattenPayload(teleDataRaw, 'dataUpdate');
      this.telemetryDataSubject.next(this.telemetryData as TelemetryData);
    }

    // 3. eventUpdatesSub
    if (payloadData.eventUpdatesSub?.payload) {
      const eventDataRaw = payloadData.eventUpdatesSub;
      eventDataRaw.payload = JSON.parse(eventDataRaw.payload);
      const eventData = this.flattenPayload(eventDataRaw, 'eventUpdate');
      this.eventUpdatesEventSubject.next(eventData);
    }

    // 4. cmdRespSub
    if (payloadData.cmdRespSub?.payload) {
      const cmdDataRaw = payloadData.cmdRespSub;
      cmdDataRaw.payload = JSON.parse(cmdDataRaw.payload);
      const cmdData = this.flattenPayload(cmdDataRaw, 'cmdUpdate');
      this.cmdRespEventSubject.next(cmdData);
    }
  }

  // Fetch data with dataUpdatesSub query
  dataUpdatesSubQuery(deviceId: string) {
    // deviceId ='TESTM0000000000002'; // controller Id
    if (!this.ws) return;
    const queryMessage = {
      id: this.generateUniqueId(), // can be any unique ID or generated ID
      payload: {
        data: JSON.stringify({
          query: `subscription MySubscription {
            dataUpdatesSub(deviceId: "${deviceId}") {
              deviceId,
              name,
              equipmentId,
              equipmentType,
              dataModelVersion,
              namespace,
              payload,
              timestamp
            }
          }`,
          variables: {}
        }),
        extensions: {
          authorization: {
            Authorization: `Bearer ${this.utilService.getToken('idToken')}`,
            host: environment.GraphQL.endpoint
          }
        }
      },
      type: 'start'
    };
    this.ws.send(JSON.stringify(queryMessage));
  }

  stateUpdatesSubQuery(deviceId: string) {
    if (!this.ws) return;

    const queryMessage = {
      id: this.generateUniqueId(), // can be any unique ID or generated ID
      payload: {
        data: JSON.stringify({
          query: `subscription MySubscription {
            stateUpdatesSub(deviceId: "${deviceId}") {
              asyncEvents {
                context,
                payload,
                eventType,
                dataModelVersion,
                timestamp
              }
            }
          }`,
          variables: {},
        }),
        extensions: {
          authorization: {
            Authorization: `Bearer ${this.utilService.getToken('idToken')}`,
            host: environment.GraphQL.endpoint
          }
        }
      },
      type: 'start',
    };
    this.ws.send(JSON.stringify(queryMessage));
  }

  cmdRespSubQuery(deviceId: string) {
    if (!this.ws) return;

    const queryMessage = {
      id: this.generateUniqueId(), // can be any unique ID or generated ID
      payload: {
        data: JSON.stringify({
          query: `subscription MySubscription {
            cmdRespSub(deviceId: "${deviceId}") {
              payload,
              timestamp
            }
          }`,
          variables: {},
        }),
        extensions: {
          authorization: {
            Authorization: `Bearer ${this.utilService.getToken('idToken')}`,
            host: environment.GraphQL.endpoint
          }
        }
      },
      type: 'start',
    };
    this.ws.send(JSON.stringify(queryMessage));
  }

  eventUpdatesSubQuery(deviceId: string) {
    if (!this.ws) return;

    const queryMessage = {
      id: this.generateUniqueId(), // can be any unique ID or generated ID
      payload: {
        data: JSON.stringify({
          query: `subscription MySubscription {
            eventUpdatesSub(deviceId: "${deviceId}") {
              deviceId,
              dataModelVersion,
              payload,
              context
            }
          }`,
          variables: {},
        }),
        extensions: {
          authorization: {
            Authorization: `Bearer ${this.utilService.getToken('idToken')}`,
            host: environment.GraphQL.endpoint
          }
        }
      },
      type: 'start',
    };
    this.ws.send(JSON.stringify(queryMessage));
  }

  // ----- Helper Functions -----
  generateUniqueId(): string {
    const randomPart = Math.floor(10000 + Math.random() * 90000);
    const timestampPart = Date.now(); // Current timestamp for uniqueness
    return `NGA${randomPart}${timestampPart}`;
    // );
  }

  flattenPayload(data: any,type: string): any {
    if (!data || !data.payload) return data;

    const { payload } = data;

    switch (type) {
      case 'dataUpdate':
        return {
          name: data.name,
          timestamp: data.timestamp,
          dataModelVersion: data.dataModelVersion,
          ...payload
        };
      case 'eventUpdate':
        return {
          context: data.context,
          deviceId: data.deviceId,
          dataModelVersion: data.dataModelVersion,
          ...payload
        };
      case 'cmdUpdate':
        return {
          timestamp: data.timestamp,
          ...payload
        };
      default:
        return data;
      }
  }

  // Subject emitter starts
  getTelemetryData(): Observable<TelemetryData | null> {
    return this.telemetryDataSubject.asObservable();
  }

  getStatusEvent(): Observable<AsyncEvent | null> {
    return this.asyncStatusSubject.asObservable();
  }

  getCmdEvent(): Observable<AsyncEvent | null> {
    return this.cmdRespEventSubject.asObservable();
  }

  getEventUpdateEvent(): Observable<AsyncEvent | null> {
    return this.eventUpdatesEventSubject.asObservable();
  }

  // Method to close connection
  disconnect() {
    if (this.ws) {
      this.ws.close();
      console.log('WebSocket Disconnected');
    }
  }
}